import React, { useState } from 'react';
import CardImage2 from '@/components/reading/CardImage2';
import { cards } from '@/constants/cards.json';
import { Box, SwipeableDrawer, Tabs, Tab } from '@mui/material';
import CardDialog from '@/components/reading/CardDialog';
import { useCardCategories, getCardsByCategory } from '@/pages/CardsMeaning/cardCategories';
import { useCardTranslations } from '@/utils/cardTranslations';

interface CardMeaningModalProps {
    card: {
        id: number;
        card_name: string;
        primary_meaning: string;
        interpretation: string;
    };
    onClose: () => void;
}

const CardsMeaning: React.FC = () => {
    const [selectedCard, setSelectedCard] = useState<typeof cards[0] | null>(null);
    const [selectedCategory, setSelectedCategory] = useState('major-arcana');
    const { getCardName } = useCardTranslations();
    const cardCategories = useCardCategories();

    const handleCardClick = (card: typeof cards[0]) => {
        setSelectedCard(card);
    };

    const handleCategoryChange = (event: React.SyntheticEvent, newValue: string) => {
        setSelectedCategory(newValue);
    };

    const filteredCards = getCardsByCategory(cards, selectedCategory);

    return (
        <div className="flex flex-col w-[calc(100%+32px)] min-h-screen -ml-[16px] -mt-[10px]">
            <Box sx={{
                position: 'fixed',
                top: '64px',
                left: 0,
                right: 0,
                width: '100%',
                borderBottom: 1,
                borderColor: 'divider',
                backgroundColor: '#F5F3F0',
                zIndex: 40
            }}>
                <Tabs
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{
                        '& .MuiTabs-scrollButtons': {
                            '&.Mui-disabled': { opacity: 0.3 }
                        },
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            minWidth: 'auto',
                            padding: '0px 0px',
                            color: '#1B2937',
                            '&.Mui-selected': {
                                color: '#1B2937',
                                fontWeight: 600
                            }
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: '#1B2937'
                        }
                    }}
                >
                    {cardCategories.map((category) => {
                        const Icon = category.icon;
                        return (
                            <Tab
                                key={category.label}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, fontWeight: 500 }}>
                                        <Icon className="w-4 h-4" />
                                        {category.displayName}
                                    </Box>
                                }
                                value={category.label}
                            />
                        );
                    })}
                </Tabs>
            </Box>
            <div className="container mx-auto px-4 py-2" style={{ marginTop: '80px' }}>
                <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-3 gap-4">
                    {filteredCards.map((card) => (
                        <div
                            key={card.id}
                            className="cursor-pointer transform hover:scale-105 transition-transform duration-200"
                            onClick={() => handleCardClick(card)}
                        >
                            <CardImage2
                                id={card.id}
                                visible={true}
                                width={120}
                                description={{
                                    ...card,
                                    card_name: getCardName(card.id, card.card_name)
                                }}
                            />
                        </div>
                    ))}
                </div>
                <SwipeableDrawer
                    anchor="bottom"
                    open={!!selectedCard}
                    onClose={() => setSelectedCard(null)}
                    onOpen={() => { }}
                    disableSwipeToOpen={true}
                    slotProps={{
                        paper: {
                            sx: {
                                height: '700px',
                                borderTopLeftRadius: '16px',
                                borderTopRightRadius: '16px',
                                padding: '20px'
                            }
                        }
                    }}
                >
                    {selectedCard && (
                        <Box sx={{ p: 0 }}>
                            <CardDialog
                                open={!!selectedCard}
                                onClose={() => setSelectedCard(null)}
                                card={{
                                    ...selectedCard,
                                    card_name: getCardName(selectedCard.id, selectedCard.card_name),
                                    reversed: false
                                }}
                            />
                        </Box>
                    )}
                </SwipeableDrawer>
            </div>
        </div>
    );
};

export default CardsMeaning;
